/**
 * InstallPromptDialog
 * Custom dialog to prompt users to install the PWA
 */

import { Download, Check, Smartphone } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'

interface InstallPromptDialogProps {
  open: boolean
  onInstall: () => void
  onDismiss: () => void
}

export function InstallPromptDialog({ open, onInstall, onDismiss }: InstallPromptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Download className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            ¿Instalar ProFessionals?
          </DialogTitle>
          <DialogDescription className="text-center">
            Accede más rápido y disfruta de una mejor experiencia instalando nuestra aplicación
          </DialogDescription>
        </DialogHeader>

        {/* Benefits List */}
        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Acceso instantáneo</p>
              <p className="text-xs text-muted-foreground">
                Abre la app directamente desde tu pantalla de inicio
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Funciona sin conexión</p>
              <p className="text-xs text-muted-foreground">
                Consulta profesionales incluso sin internet
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Smartphone className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Experiencia nativa</p>
              <p className="text-xs text-muted-foreground">
                Interfaz optimizada como una app móvil
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <Button onClick={onInstall} className="w-full" size="lg">
            <Download className="mr-2 h-4 w-4" />
            Instalar Ahora
          </Button>
          <Button onClick={onDismiss} variant="ghost" className="w-full">
            Ahora No
          </Button>
        </div>

        {/* Small Note */}
        <p className="text-center text-xs text-muted-foreground">
          Podrás instalarla más tarde desde el menú
        </p>
      </DialogContent>
    </Dialog>
  )
}
